import { useCallback, useMemo, useState } from 'react';
import {
  decodeJwtPayload,
  redirectToWorkspaceSubdomainWithHandoff,
  useAuth,
} from '@aimsos/auth';
import { useWorkspaces } from '../../../hooks/useWorkspaces.js';
import { synthesizeWorkspaceMeta } from '../__mocks__/workspaceSelector.mock.js';
import { useLastVisited } from './useLastVisited.js';
import { sortWorkspaces } from '../utils/sortWorkspaces.js';
import { SORT_MODES, studioRoute } from '../workspaceSelectorData.js';

/**
 * Data layer for the Workspace Selector. Wraps `useWorkspaces()` and:
 *
 * - overlays the mock industry / role / studio-access / last-visited metadata
 *   by workspace id (see `__mocks__/workspaceSelector.mock.js`),
 * - splits the list into the active workspace (JWT `tenant_uuid`) and the rest,
 * - exposes a single `openWorkspace(id, studioId?)` action that works for the
 *   active workspace too (see below),
 * - passes `hasMore` / `loadMore` / `totalCount` straight through so the list's
 *   "Show N more" can actually reach beyond the first API page,
 * - exposes the search field's state (CT-4938). That search is the hook's own
 *   SERVER-side one (`POST /v0/workspace/list` `search.keyword`, debounced
 *   500ms), so it reaches every workspace and not just the pages already
 *   fetched. There is deliberately NO client-side filter on top of it.
 * - owns the sort mode and applies it to the non-active rows (CT-4939). Unlike
 *   the search, sorting IS client-side and therefore only covers the loaded
 *   pages — see `utils/sortWorkspaces.js` for why and what that costs.
 */
export function useWorkspaceSelectorData() {
  const { user, token } = useAuth();
  const {
    tenants,
    resultTenants,
    currentTenantId,
    isLoading,
    isRefetching,
    isLoadingMore,
    hasMore,
    loadMore,
    totalCount,
    searchKeyword,
    onSearchChange,
    switchTo,
    isSwitching,
    switchError,
  } = useWorkspaces();

  const [switchingId, setSwitchingId] = useState(null);
  const [sortMode, setSortMode] = useState(SORT_MODES[0]);

  const hasQuery = Boolean(searchKeyword.trim());

  // While a query is active the shared hook's `tenants` is the wrong list: it
  // pins the active workspace to the top whatever the query, and substitutes a
  // synthetic placeholder row when nothing matched. Both exist so the Topbar
  // chip always resolves to something, and both would lie in a result list.
  const rows = hasQuery ? resultTenants : tenants;

  const metaById = useMemo(() => {
    const map = {};
    rows.forEach((ws) => {
      map[ws.id] = synthesizeWorkspaceMeta(ws, { isActive: ws.id === currentTenantId });
    });
    return map;
  }, [rows, currentTenantId]);

  const { active, others } = useMemo(() => {
    let activeWs = null;
    const rest = [];
    rows.forEach((ws) => {
      if (ws.id === currentTenantId) activeWs = ws;
      else rest.push(ws);
    });
    // The active workspace is pinned above the list in every mode, so only the
    // rest is sorted.
    return { active: activeWs, others: sortWorkspaces(rest, sortMode, metaById) };
  }, [rows, currentTenantId, sortMode, metaById]);

  // Resume banner (CT-4942). Resolved against the UNFILTERED list and the true
  // active workspace: "where you left off" is a fact about the session, not
  // about whatever the current search matched.
  const trueActive = useMemo(
    () => tenants.find((ws) => ws.id === currentTenantId) ?? null,
    [tenants, currentTenantId],
  );
  const lastVisited = useLastVisited(trueActive, tenants);

  // "You have access to N workspaces" must not follow the query — `totalCount`
  // becomes the MATCH count while searching. Hold the last unfiltered total.
  // Adjusted while rendering rather than in an effect: the count is read in the
  // same render, so an effect showed the previous total for one frame.
  const [accessCount, setAccessCount] = useState(0);
  if (!hasQuery && totalCount && accessCount !== totalCount) {
    setAccessCount(totalCount);
  }

  /**
   * Enter a workspace, optionally deep-linking into one of its studios.
   *
   * For any workspace OTHER than the active one this is just `switchTo()`:
   * exchange the JWT for one scoped to that workspace, then hand it off to
   * that workspace's subdomain.
   *
   * The active workspace needs the extra branch. `switchTo()` deliberately
   * no-ops when the target id already matches `currentTenantId` (correct for
   * the in-shell workspace switcher — there is nothing to switch), but here we
   * are still sitting on the base-domain picker, NOT inside that workspace
   * yet, so the no-op left the primary card dead: clicking it did nothing at
   * all. Hand the EXISTING token off directly instead — the same workaround
   * `WorkspaceGate` already applies for a pending deep-link redirect.
   */
  const openWorkspace = useCallback(
    (workspaceId, studioId) => {
      if (!workspaceId || isSwitching) return;
      const path = studioId ? studioRoute(studioId) : undefined;
      setSwitchingId(workspaceId);

      if (workspaceId !== currentTenantId) {
        switchTo(workspaceId, path ? { path } : undefined);
        return;
      }

      const target = rows.find((ws) => ws.id === workspaceId);
      const slug = target?.slug || decodeJwtPayload(token)?.workspace_slug;
      const profile = user
        ? { name: user.name, email: user.email, picture: user.picture }
        : undefined;
      const redirected = redirectToWorkspaceSubdomainWithHandoff(
        slug,
        { token, profile },
        { path: path || '/' },
      );
      // No root domain configured for this environment (so subdomain routing
      // is a no-op) — just navigate on this origin, same fallback switchTo uses.
      if (!redirected) globalThis.location.href = path || '/';
    },
    [isSwitching, currentTenantId, switchTo, rows, token, user],
  );

  // Skeleton on the very first fetch, and on a refetch with nothing to show.
  // NOT gated on `tenants.length === 0`: useWorkspaces() injects a synthetic
  // {name: 'Workspace', initials: 'WS'} placeholder while loading so the
  // topbar chip is never blank, which means the list is never actually empty —
  // gating on it flashed that fake card instead of the skeleton.
  const showSkeleton = isLoading || (isRefetching && rows.length === 0);

  return {
    active,
    others,
    metaById,
    /** Unfiltered "you have access to N" count; 0 hides the line. */
    accessCount: showSkeleton ? 0 : accessCount,
    /** Total for the CURRENT query — drives "Show N more". */
    totalCount,
    hasQuery,
    lastVisited,
    searchKeyword,
    onSearchChange,
    sortMode,
    onSortChange: setSortMode,
    isLoading: showSkeleton,
    isLoadingMore,
    hasMore,
    loadMore,
    openWorkspace,
    switchingId: isSwitching ? switchingId : null,
    switchError,
  };
}
